import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "@/hooks/useApiMutation";
import { apiRequest } from "@/lib/queryClient";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, ArrowLeft, Save, Loader2, GripVertical, X } from "lucide-react";

const SECTION_HEADINGS = ["Overview", "Steps/Rules", "Examples", "Do/Don't", "Formulas", "Alerts", "Custom"] as const;
const CONTENT_TYPES = [
  { value: "recipe_knowledge", label: "Recipe Knowledge" },
  { value: "business_knowledge", label: "Business Knowledge" },
  { value: "core_principle", label: "Core Principle" },
  { value: "operational", label: "Operational" },
  { value: "general", label: "General" },
];
const VISIBILITY_OPTIONS = [
  { value: "public", label: "Public" },
  { value: "students", label: "Students" },
  { value: "enrolled_only", label: "Enrolled Only" },
  { value: "internal", label: "Internal" },
];
const RAG_TARGETS = [
  { value: "assistant", label: "AI Assistant" },
  { value: "sous_chef", label: "AI Sous Chef" },
];

interface Section {
  heading: string;
  customHeading?: string;
  content: string;
}

interface FormData {
  title: string;
  category: string;
  topic: string;
  contentType: string;
  tags: string[];
  visibility: string;
  status: string;
  sections: Section[];
  ragTargets: string[];
}

const defaultForm: FormData = {
  title: "", category: "", topic: "", contentType: "general",
  tags: [], visibility: "internal", status: "draft",
  sections: [{ heading: "Overview", content: "" }],
  ragTargets: ["assistant"],
};

interface KBEditorProps {
  entryId: string | null;
  onBack: () => void;
}

export function KBArticleEditor({ entryId, onBack }: KBEditorProps) {
  const [form, setForm] = useState<FormData>(defaultForm);
  const [tagInput, setTagInput] = useState("");
  const queryClient = useQueryClient();

  const { data: entry, isLoading } = useQuery<any>({
    queryKey: ["/api/knowledge-base", entryId],
    queryFn: () => apiRequest("GET", `/api/knowledge-base/${entryId}`),
    enabled: !!entryId,
  });

  useEffect(() => {
    if (entry) {
      setForm({
        title: entry.title || "",
        category: entry.category || "",
        topic: entry.topic || "",
        contentType: entry.contentType || "general",
        tags: entry.tags || [],
        visibility: entry.visibility || "internal",
        status: entry.status || "draft",
        sections: entry.sections?.length ? entry.sections : [{ heading: "Overview", content: "" }],
        ragTargets: entry.ragTargets || ["assistant"],
      });
    }
  }, [entry]);

  const saveMutation = useApiMutation({
    endpoint: entryId ? `/api/knowledge-base/${entryId}` : "/api/knowledge-base/article",
    method: entryId ? "PATCH" : "POST",
    invalidate: [["/api/knowledge-base"]],
    successMessage: entryId ? "Article updated" : "Article created",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/knowledge-base", entryId] });
      if (!entryId) onBack();
    },
  });

  const handleSave = (asStatus?: string) => {
    if (!form.title.trim()) { showError("Title is required"); return; }
    saveMutation.mutate({ ...form, status: asStatus || form.status });
  };

  const addSection = () => {
    setForm(f => ({ ...f, sections: [...f.sections, { heading: "Custom", customHeading: "", content: "" }] }));
  };

  const removeSection = (idx: number) => {
    setForm(f => ({ ...f, sections: f.sections.filter((_, i) => i !== idx) }));
  };

  const updateSection = (idx: number, field: string, value: string) => {
    setForm(f => ({
      ...f,
      sections: f.sections.map((s, i) => i === idx ? { ...s, [field]: value } : s),
    }));
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setForm(f => ({ ...f, tags: [...f.tags, tag] }));
      setTagInput("");
    }
  };

  const toggleRagTarget = (target: string) => {
    setForm(f => ({
      ...f,
      ragTargets: f.ragTargets.includes(target)
        ? f.ragTargets.filter(t => t !== target)
        : [...f.ragTargets, target],
    }));
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={onBack}><ArrowLeft className="h-4 w-4" /></Button>
        <h2 className="text-xl font-bold">{entryId ? "Edit Article" : "New Article"}</h2>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => handleSave("draft")} disabled={saveMutation.isPending}>
            Save as Draft
          </Button>
          <Button onClick={() => handleSave("active")} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Publish & Embed
          </Button>
        </div>
      </div>

      {/* Metadata */}
      <Card>
        <CardHeader><CardTitle>Article Details</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g., Bread Baking Fundamentals" />
            </div>
            <div className="space-y-2">
              <Label>Topic</Label>
              <Input value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} placeholder="e.g., Fermentation, Costing" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Input value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g., Baking, Business" />
            </div>
            <div className="space-y-2">
              <Label>Content Type</Label>
              <Select value={form.contentType} onValueChange={v => setForm(f => ({ ...f, contentType: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTENT_TYPES.map(ct => <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Visibility</Label>
              <Select value={form.visibility} onValueChange={v => setForm(f => ({ ...f, visibility: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VISIBILITY_OPTIONS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>RAG Targets</Label>
              <div className="flex gap-2">
                {RAG_TARGETS.map(t => (
                  <Button key={t.value} variant={form.ragTargets.includes(t.value) ? "default" : "outline"} size="sm"
                    onClick={() => toggleRagTarget(t.value)}>
                    {t.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2">
              <Input value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag..."
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} className="max-w-xs" />
              <Button variant="outline" size="sm" onClick={addTag}>Add</Button>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setForm(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }))} />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sections */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Content Sections</CardTitle>
          <Button variant="outline" size="sm" onClick={addSection}><Plus className="h-4 w-4 mr-1" /> Add Section</Button>
        </CardHeader>
        <CardContent className="space-y-6">
          {form.sections.map((section, idx) => (
            <div key={idx} className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Select value={section.heading} onValueChange={v => updateSection(idx, "heading", v)}>
                  <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SECTION_HEADINGS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                  </SelectContent>
                </Select>
                {section.heading === "Custom" && (
                  <Input value={section.customHeading || ""} onChange={e => updateSection(idx, "customHeading", e.target.value)}
                    placeholder="Custom heading..." className="flex-1" />
                )}
                {form.sections.length > 1 && (
                  <Button variant="ghost" size="icon" onClick={() => removeSection(idx)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <Textarea
                value={section.content}
                onChange={e => updateSection(idx, "content", e.target.value)}
                rows={6}
                placeholder={`Write ${section.heading === "Custom" ? section.customHeading || "custom" : section.heading} content...`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Status info */}
      {entryId && entry && (
        <div className="text-sm text-muted-foreground">
          Status: <Badge variant={entry.status === "active" ? "default" : "secondary"}>{entry.status}</Badge>
          {" • "}{entry.chunks?.length || 0} chunks embedded
          {entry.errorMessage && <span className="text-destructive"> • Error: {entry.errorMessage}</span>}
        </div>
      )}
    </div>
  );
}
